//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
// one-on-one-autopilot imports
import { IColumnsViewProps, IState, IColumn } from "./OneOnOneAutopilot.d";
import { OnboardingAutopilotEvent } from "@components/onboarding-autopilot-event";
import { styles } from "./OneOnOnAutopilot.styles";
// other components
import { H2 } from "@ui";
// util
import { getSetMoveRange } from "@clockwise/client-commons/src/util/event";
// lodash
import debounce from "lodash/debounce";
// material-ui
import withStyles from "@material-ui/core/styles/withStyles";

// interval >= 1 matches as-is
// interval of -1 matches any interval
// interval of -2 matches any interval greater than 1, which is used to match less frequent monthly events.
// interval of -3 matches any interval greater than 4, which is used to match less frequent weekly events.
const RECURRENCE_CATEGORY_SELECTORS = [
  {
    header: "Weekly",
    selectors: [
      { type: "Weekly", interval: 1 },
      { type: "Daily", interval: -1 },
    ],
  },
  { header: "Biweekly", selectors: [{ type: "Weekly", interval: 2 }] },
  { header: "Every Three Weeks", selectors: [{ type: "Weekly", interval: 3 }] },
  {
    header: "Monthly",
    selectors: [
      { type: "Monthly", interval: 1 },
      { type: "Weekly", interval: 4 },
    ],
  },
  {
    header: "Less Frequent",
    selectors: [
      { type: "Yearly", interval: -1 },
      { type: "Monthly", interval: -2 },
      { type: "Weekly", interval: -3 },
    ],
  },
];

//////////////////
// COMPONENT
//////////////////

class OneOnOneAutopilotColumnsComponent extends React.Component<IColumnsViewProps, IState> {
  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  private onAutopilotToggleDebounced: (enabled: boolean, externalEventId: string) => void;

  constructor(props: IColumnsViewProps) {
    super(props);

    this.onAutopilotToggleDebounced = debounce(props.onAutopilotToggle, 250, { leading: true });
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-

  getEventsWithFrequencyType(
    events: ISchema.IEvent[],
    selectors: { type: string; interval: number }[],
  ) {
    return selectors
      .map((selector) =>
        events.filter((e) => {
          const rules = e.recurrenceRules;
          // See comment above regarding RECURRENCE_CATEGORY_SELECTORS for how selector.interval is used.
          return (
            rules &&
            rules.interval &&
            rules.interval.freqType === selector.type &&
            (selector.interval === -1 ||
              (selector.interval === -2 && rules.interval.interval > 1) ||
              (selector.interval === -3 && rules.interval.interval > 4) ||
              rules.interval.interval === selector.interval)
          );
        }),
      )
      .flat();
  }

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  renderEvent(event: ISchema.IEvent) {
    const { org, eventsApEnabled, canAutopilotMap, selectedFlex } = this.props,
      externalEventId = event.eventKey.externalEventId;

    const apEnabled = eventsApEnabled[externalEventId].enabled;

    const moveRange = getSetMoveRange(event);

    return (
      <div style={styles.eventContainer} key={externalEventId}>
        <OnboardingAutopilotEvent
          autopilotEnabled={apEnabled}
          allowSimilarRooms={true}
          onAutopilotToggle={this.onAutopilotToggleDebounced}
          org={org}
          event={event}
          personMap={{}}
          action={"autopilot-simple"}
          enableTracking={false}
          isDisabled={!!!canAutopilotMap[externalEventId]}
          allowedStart={moveRange?.allowedStartTime ?? undefined}
          allowedEnd={moveRange?.allowedEndTime ?? undefined}
          selectedFlex={selectedFlex}
        />
      </div>
    );
  }

  renderColumn(column: IColumn) {
    const { classes, maxEventsPerColumn } = this.props;

    const getEventsPerColumn = (events: ISchema.IEvent[]) => {
      if (!!maxEventsPerColumn && events.length > maxEventsPerColumn) {
        return events.slice(0, maxEventsPerColumn);
      } else {
        return events;
      }
    };
    return (
      <div
        style={styles.column}
        key={column.categories[0].header.toString()}
        className="cw-bg-gray-50 cw-border cw-rounded-lg cw-border-grey-lightest cw-pt-4 cw-pb-2 cw-px-1 cw-border-solid cw-border-gray-200"
      >
        <div>
          {column.categories.map((category, i) => [
            <H2
              style={i === 0 ? styles.columnHeader : styles.columnHeaderSecond}
              className={i === 0 ? classes.columnHeader : classes.columnHeaderSecond}
              key={category.header.toString()}
            >
              {category.header}
            </H2>,
            getEventsPerColumn(category.events).map((event: ISchema.IEvent) =>
              this.renderEvent(event),
            ),
          ])}
        </div>
      </div>
    );
  }

  reduceColumns(columns: IColumn[], maxColumns: number = 3, maxPerColumn: number = 3) {
    // If we have more recurrence categories/columns than we desire (have room for), determine
    // which two should be combined to create the smallest resultant column, and repeat said
    // process until we reach targetNumColumns.
    for (let numToReduce = columns.length - maxColumns; numToReduce > 0; numToReduce--) {
      const scores = [];
      scores.length = columns.length - 1; // index (score) i is cardinality of combining columns[i] with columns[i+1]
      for (let i = 0; i < columns.length - 1; i++) {
        if (columns[i].categories.length + columns[i + 1].categories.length <= maxPerColumn) {
          scores[i] = columns[i].categories
            .concat(columns[i + 1].categories)
            .reduce((accum, c) => accum + c.events.length, 0);
        } // else, no score for this index. Would have excceded maxPerColumn.
      }
      let min = Infinity,
        winner = scores.length - 1; // If no scores and winner found, default to combining the last two columns.
      // Iterate backwards to prioritize combining less frequent columns (combine monthly and tri weekly before weekly and biweekly)
      for (let i = scores.length - 1; i >= 0; i--) {
        if (scores[i] && scores[i] < min) {
          min = scores[i];
          winner = i;
        }
      }
      // Combine col[winner] and col[winner+1] categories into one column.
      columns[winner].categories.push(...columns[winner + 1].categories);
      columns.splice(winner + 1, 1);
    }

    return columns;
  }

  public render() {
    const { oneOnOnesForCalendar, classes } = this.props;

    if (oneOnOnesForCalendar.length === 0) {
      return <div style={styles.noOneOnOnesImg} className={classes.noOneOnOnesImg}></div>;
    }

    const recurrenceCategories = RECURRENCE_CATEGORY_SELECTORS.map((category) => ({
      events: this.getEventsWithFrequencyType(oneOnOnesForCalendar, category.selectors),
      header: category.header,
    })).filter((freq) => freq.events.length > 0);

    const columnsToRender = this.reduceColumns(
      recurrenceCategories.map((category) => ({ categories: [category] } as IColumn)),
    );

    return (
      <div style={styles.allColumns} className={classes.allColumns + " cw-gap-4"}>
        {columnsToRender.map((col: IColumn) => this.renderColumn(col))}
      </div>
    );
  }
}

//////////////////
// STYLES
//////////////////

export const OneOnOneAutopilotColumnsView = withStyles(styles)(OneOnOneAutopilotColumnsComponent);
